(function () {
  'use strict';

  window.bloomingfulWsaioLivePricingVersion = 'wsaio-live-pricing-20260924-6';
  var endpoint = '/apps/bloomingful-projects/wsaio-pricing';
  var batchSize = 75;
  var batchConcurrency = 3;
  var requestTimeoutMs = 15000;
  var refreshTimer = null;
  var requestInFlight = null;
  var lastSignature = '';
  var lastLoadedAt = 0;
  var queuedRefresh = false;

  function productIds() {
    var ids = [];
    function add(id) {
      id = String(id || '');
      if (/^\d+$/.test(id) && ids.indexOf(id) === -1) ids.push(id);
    }

    // Cart products take priority so drawer pricing never depends on the page being viewed.
    var cartItems = window.cart_data && Array.isArray(window.cart_data.items)
      ? window.cart_data.items
      : [];
    cartItems.forEach(function (item) { add(item && item.product_id); });
    document.querySelectorAll('[data-product-id]').forEach(function (element) {
      add(element.getAttribute('data-product-id'));
    });
    return ids;
  }

  function mergeRules(currentRules, liveRules) {
    var merged = [];
    var liveByKey = {};

    (Array.isArray(liveRules) ? liveRules : []).forEach(function (rule) {
      var key = String(rule && rule.key || '');
      if (key) liveByKey[key] = rule;
      else merged.push(rule);
    });
    (Array.isArray(currentRules) ? currentRules : []).forEach(function (rule) {
      var key = String(rule && rule.key || '');
      if (key && liveByKey[key]) {
        merged.push(liveByKey[key]);
        delete liveByKey[key];
      } else {
        merged.push(rule);
      }
    });
    Object.keys(liveByKey).forEach(function (key) { merged.push(liveByKey[key]); });
    return merged;
  }

  function mergePricing(payload) {
    var current = window.bloomingfulWsaioStorefrontPricing || {};
    var currentCollections = current.productCollectionsById || {};
    var liveCollections = payload.productCollectionsById || {};

    window.bloomingfulWsaioStorefrontPricing = Object.assign({}, current, {
      generatedAt: payload.generatedAt || new Date().toISOString(),
      pricingRules: Array.isArray(payload.pricingRules) ? payload.pricingRules : [],
      regularDiscountRules: Array.isArray(payload.regularDiscountRules) ? payload.regularDiscountRules : [],
      productCollectionsById: Object.assign({}, currentCollections, liveCollections),
      source: 'live-wsaio-metafields'
    });
    document.documentElement.setAttribute('data-bf-wsaio-live-pricing', 'ready');
    document.dispatchEvent(new CustomEvent('bloomingful:wsaio-pricing-updated', {
      detail: {
        generatedAt: window.bloomingfulWsaioStorefrontPricing.generatedAt,
        pricingRules: window.bloomingfulWsaioStorefrontPricing.pricingRules.length,
        regularDiscountRules: window.bloomingfulWsaioStorefrontPricing.regularDiscountRules.length
      }
    }));
  }

  function chunks(values, size) {
    var result = [];
    for (var index = 0; index < values.length; index += size) {
      result.push(values.slice(index, index + size));
    }
    return result;
  }

  function fetchBatch(ids) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timeout = window.setTimeout(function () {
      if (controller) controller.abort();
    }, requestTimeoutMs);
    var options = {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    };
    if (controller) options.signal = controller.signal;

    return fetch(endpoint + '?product_ids=' + encodeURIComponent(ids.join(',')), options).then(function (response) {
      if (!response.ok) throw new Error('Live WSAIO pricing request failed: ' + response.status);
      return response.json();
    }).then(function (payload) {
      if (!payload || payload.ok !== true || !Array.isArray(payload.pricingRules)) {
        throw new Error('Live WSAIO pricing response was invalid.');
      }
      return payload;
    }).finally(function () {
      window.clearTimeout(timeout);
    });
  }

  function fetchBatchWithRetry(ids, attemptsRemaining) {
    return fetchBatch(ids).catch(function (error) {
      if (attemptsRemaining <= 1) throw error;
      return fetchBatchWithRetry(ids, attemptsRemaining - 1);
    });
  }

  function runBatchPool(batches, concurrency) {
    var results = new Array(batches.length);
    var cursor = 0;

    function worker() {
      var index = cursor;
      cursor += 1;
      if (index >= batches.length) return Promise.resolve();
      return fetchBatchWithRetry(batches[index], 2).then(function (payload) {
        results[index] = payload;
        return worker();
      });
    }

    var workers = [];
    var workerCount = Math.min(concurrency, batches.length);
    for (var index = 0; index < workerCount; index += 1) workers.push(worker());
    return Promise.all(workers).then(function () { return results; });
  }

  function aggregatePayloads(payloads) {
    var pricingRules = [];
    var regularDiscountRules = [];
    var productCollectionsById = {};
    var generatedAt = '';

    payloads.forEach(function (payload) {
      generatedAt = payload.generatedAt || generatedAt;
      pricingRules = mergeRules(pricingRules, payload.pricingRules);
      regularDiscountRules = mergeRules(regularDiscountRules, payload.regularDiscountRules);
      Object.assign(productCollectionsById, payload.productCollectionsById || {});
    });

    return {
      ok: true,
      generatedAt: generatedAt || new Date().toISOString(),
      pricingRules: pricingRules,
      regularDiscountRules: regularDiscountRules,
      productCollectionsById: productCollectionsById
    };
  }

  function fetchPricing(ids) {
    var batches = chunks(ids, batchSize);
    var firstBatch = batches.shift();

    // Warm the server-side WSAIO rule cache once, then verify remaining products in parallel.
    return fetchBatchWithRetry(firstBatch, 2).then(function (firstPayload) {
      return runBatchPool(batches, batchConcurrency).then(function (remainingPayloads) {
        return aggregatePayloads([firstPayload].concat(remainingPayloads));
      });
    });
  }

  function settleWithFallback() {
    var current = window.bloomingfulWsaioStorefrontPricing || {};

    document.documentElement.setAttribute('data-bf-wsaio-live-pricing', 'fallback');
    document.dispatchEvent(new CustomEvent('bloomingful:wsaio-pricing-updated', {
      detail: {
        generatedAt: current.generatedAt || '',
        pricingRules: Array.isArray(current.pricingRules) ? current.pricingRules.length : 0,
        regularDiscountRules: Array.isArray(current.regularDiscountRules) ? current.regularDiscountRules.length : 0,
        fallback: true
      }
    }));
  }

  function refresh(force) {
    var ids = productIds();
    var signature = ids.join(',');
    var now = Date.now();

    if (!ids.length) {
      settleWithFallback();
      return Promise.resolve(null);
    }
    if (requestInFlight) {
      if (signature !== lastSignature) queuedRefresh = true;
      return requestInFlight;
    }
    if (!force && signature === lastSignature && now - lastLoadedAt < 30000) {
      return Promise.resolve(window.bloomingfulWsaioStorefrontPricing || null);
    }

    lastSignature = signature;
    document.documentElement.setAttribute('data-bf-wsaio-live-pricing', 'loading');
    requestInFlight = fetchPricing(ids).then(function (payload) {
      if (productIds().join(',') !== signature) {
        queuedRefresh = true;
        return window.bloomingfulWsaioStorefrontPricing || null;
      }
      lastLoadedAt = Date.now();
      mergePricing(payload);
      return window.bloomingfulWsaioStorefrontPricing;
    }).catch(function () {
      settleWithFallback();
      return window.bloomingfulWsaioStorefrontPricing || null;
    }).finally(function () {
      requestInFlight = null;
      if (queuedRefresh) {
        queuedRefresh = false;
        scheduleRefresh(false);
      }
    });

    return requestInFlight;
  }

  function scheduleRefresh(force) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(function () { refresh(Boolean(force)); }, 80);
  }

  window.bloomingfulRefreshWsaioPricing = function () { return refresh(true); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scheduleRefresh(false); });
  } else {
    scheduleRefresh(false);
  }
  ['bloomingful:collection-hydrated', 'collection:reloaded', 'collection:updated'].forEach(function (eventName) {
    document.addEventListener(eventName, function () { scheduleRefresh(false); });
  });
  ['ajaxProduct:added', 'cart:rebuilt', 'cart:quantity', 'cart:build', 'cart:updated'].forEach(function (eventName) {
    document.addEventListener(eventName, function () { scheduleRefresh(false); });
  });
  window.addEventListener('pageshow', function () { scheduleRefresh(false); });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && Date.now() - lastLoadedAt >= 5 * 60 * 1000) scheduleRefresh(true);
  });
})();
